import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { canAccessOzelDosyalar } from '../utils/ozelDosyaAccess';
import { epostaAPI, bakisAPI } from '../api/api';
import { davaTuruEtiketi } from '../utils/constants';
import './GelenPostalar.css';
import { onayla } from '../utils/onayla';

/**
 * Gelen Postalar — e-posta kutusundaki yazıları dosyalarla eşleştirip
 * iş listesine aktarma ekranı.
 *
 * TASARIMIN ÖZÜ: hiçbir posta kendiliğinden iş listesine düşmez. Sistem
 * yalnızca "bu posta şu dosyaya ait görünüyor" der; iş kaydını siz
 * onaylayınca oluşturur. Bir tebligat süresinin yanlış hesaplanması ağır
 * sonuç doğurduğu için son söz insanda kalıyor.
 *
 * Posta kutusu artık arka planda kendiliğinden kontrol ediliyor
 * (EpostaOtomatikKontrol); "Şimdi Kontrol Et" düğmesi yalnızca beklemek
 * istemediğinizde.
 */

const ESLESME_ETIKET = {
  esas: 'Esas numarasıyla eşleşti',
  icra: 'İcra dosya numarasıyla eşleşti',
  tahkim: 'Tahkim numarasıyla eşleşti',
  foy: 'Föy numarasıyla eşleşti',
  isim: 'Müvekkil adıyla eşleşti',
  belirsiz: 'Birden fazla dosya eşleşti',
  yok: 'Dosya bulunamadı',
};

/** Tarih süzgeci seçenekleri. Değer = kaç gün geriye bakılacağı. */
const GUN_SECENEKLERI = [
  { deger: 1, ad: 'Bugün' },
  { deger: 7, ad: 'Bu hafta' },
  { deger: 15, ad: '15 gün' },
  { deger: 30, ad: '30 gün' },
  { deger: 0, ad: 'Hepsi' },
];

/** Son seçilen süzgeç tarayıcıda saklanır; her girişte yeniden seçilmesin. */
const GUN_ANAHTARI = 'gelenPostalar.gun';
const DURUM_ANAHTARI = 'gelenPostalar.durum';

const saklananOku = (anahtar, varsayilan) => {
  try {
    const v = localStorage.getItem(anahtar);
    return v === null ? varsayilan : v;
  } catch { return varsayilan; }
};
const saklananYaz = (anahtar, deger) => {
  try { localStorage.setItem(anahtar, String(deger)); } catch { /* yoksay */ }
};

const tarihYaz = (d) => {
  if (!d) return '-';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '-' : t.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** "3 dakika önce" gibi kısa bir ifade; son kontrol zamanı için. */
const gecenSure = (d) => {
  if (!d) return 'hiç';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return 'hiç';
  const dk = Math.floor((Date.now() - t.getTime()) / 60000);
  if (dk < 1) return 'az önce';
  if (dk < 60) return dk + ' dakika önce';
  const sa = Math.floor(dk / 60);
  if (sa < 24) return sa + ' saat önce';
  return Math.floor(sa / 24) + ' gün önce';
};

const GelenPostalar = () => {
  const { user } = useAuth();
  const yetkili = canAccessOzelDosyalar(user);

  const [hesap, setHesap] = useState(null);
  const [ayarAcik, setAyarAcik] = useState(false);
  const [form, setForm] = useState({
    Eposta: '', ImapSunucu: '', ImapPort: 993, SslKullan: true,
    KullaniciAdi: '', Sifre: '', Klasor: 'INBOX', Aktif: true,
  });

  const [liste, setListe] = useState([]);
  const [sayim, setSayim] = useState([]);
  const [durum, setDurum] = useState(() => saklananOku(DURUM_ANAHTARI, 'bekliyor'));
  const [gun, setGun] = useState(() => {
    const v = parseInt(saklananOku(GUN_ANAHTARI, '15'), 10);
    return GUN_SECENEKLERI.some((g) => g.deger === v) ? v : 15;
  });
  const [yukleniyor, setYukleniyor] = useState(false);
  const [okuyor, setOkuyor] = useState(false);
  const [mesaj, setMesaj] = useState('');
  const [hata, setHata] = useState('');
  const [acik, setAcik] = useState(null);
  const [sonKontrol, setSonKontrol] = useState(null);

  /** Toplu işlem için seçilen posta kimlikleri. */
  const [secililer, setSecililer] = useState(() => new Set());
  const [topluCalisiyor, setTopluCalisiyor] = useState(false);

  const mesajZamani = useRef(null);

  const bildir = useCallback((m, h = '') => {
    setMesaj(m); setHata(h);
    if (mesajZamani.current) window.clearTimeout(mesajZamani.current);
    if (m && !h) mesajZamani.current = window.setTimeout(() => setMesaj(''), 6000);
  }, []);

  const hesabiYukle = useCallback(async () => {
    try {
      const h = await epostaAPI.hesap();
      setHesap(h);
      if (h) {
        setForm((f) => ({
          ...f,
          Eposta: h.eposta ?? h.Eposta ?? '',
          ImapSunucu: h.imapSunucu ?? h.ImapSunucu ?? '',
          ImapPort: h.imapPort ?? h.ImapPort ?? 993,
          SslKullan: h.sslKullan ?? h.SslKullan ?? true,
          KullaniciAdi: h.kullaniciAdi ?? h.KullaniciAdi ?? '',
          Klasor: h.klasor ?? h.Klasor ?? 'INBOX',
          Aktif: h.aktif ?? h.Aktif ?? true,
          Sifre: '',
        }));
        setSonKontrol(h.sonKontrol ?? h.SonKontrol ?? null);
      } else {
        setAyarAcik(true);
      }
    } catch (e) {
      setHata(e.response?.data?.message || e.message);
    }
  }, []);

  const listeyiYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const v = await epostaAPI.yazismalar(durum, 100, gun);
      setListe(v.liste ?? v.Liste ?? []);
      setSayim(v.sayim ?? v.Sayim ?? []);
      setSecililer(new Set());
    } catch (e) {
      setHata(e.response?.data?.message || e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [durum, gun]);

  useEffect(() => { if (yetkili) hesabiYukle(); }, [yetkili, hesabiYukle]);
  useEffect(() => { if (yetkili) listeyiYukle(); }, [yetkili, listeyiYukle]);

  /* Sayfa acildi: menudeki rozet sifirlansin. Son bakis sunucuda
     tutuluyor, boylece telefondan girince de ayni yerden devam eder. */
  useEffect(() => {
    if (!yetkili) return;
    bakisAPI.isaretle('gelenPosta').catch(() => { /* yoksay */ });
  }, [yetkili]);

  /**
   * Arka plandaki otomatik kontrol yeni posta getirmiş olabilir; sayfa
   * açıkken iki dakikada bir sessizce bakıyoruz. Tüm listeyi çekmiyoruz,
   * yalnızca sayıyı — sayı değişmişse listeyi tazeliyoruz.
   */
  const bekleyenSayiRef = useRef(null);
  useEffect(() => {
    if (!yetkili) return undefined;
    const bak = async () => {
      try {
        const v = await epostaAPI.bekleyenSayi();
        const adet = v.adet ?? v.Adet ?? 0;
        setSonKontrol(v.sonKontrol ?? v.SonKontrol ?? null);
        if (bekleyenSayiRef.current !== null && adet !== bekleyenSayiRef.current) {
          listeyiYukle();
        }
        bekleyenSayiRef.current = adet;
      } catch { /* sessiz — arka plan kontrolü ekranı bozmamalı */ }
    };
    bak();
    const z = window.setInterval(bak, 120000);
    return () => window.clearInterval(z);
  }, [yetkili, listeyiYukle]);

  const bekleyen = sayim.find((s) => (s.durum ?? s.Durum) === 'bekliyor');

  const listeIdleri = useMemo(
    () => liste.map((y) => y.id ?? y.Id),
    [liste]
  );
  const hepsiSecili = listeIdleri.length > 0 && listeIdleri.every((i) => secililer.has(i));

  if (!yetkili) {
    return (
      <div className="gp">
        <header className="gp-basi"><h1>Gelen Postalar</h1></header>
        <p className="gp-bos">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
      </div>
    );
  }

  const durumSec = (d) => { setDurum(d); saklananYaz(DURUM_ANAHTARI, d); };
  const gunSec = (g) => { setGun(g); saklananYaz(GUN_ANAHTARI, g); };

  const secimDegistir = (id) => {
    setSecililer((ö) => {
      const y = new Set(ö);
      if (y.has(id)) y.delete(id); else y.add(id);
      return y;
    });
  };

  const hepsiniSec = () => {
    setSecililer(hepsiSecili ? new Set() : new Set(listeIdleri));
  };

  const ayarKaydet = async (e) => {
    e.preventDefault();
    try {
      await epostaAPI.hesapKaydet(form);
      bildir('Ayarlar kaydedildi.');
      setAyarAcik(false);
      await hesabiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    }
  };

  const baglantiTest = async () => {
    bildir('Bağlanılıyor…');
    try {
      const s = await epostaAPI.test();
      const ok = s.basarili ?? s.Basarili;
      const m = s.mesaj ?? s.Mesaj;
      const adet = s.mesajSayisi ?? s.MesajSayisi;
      if (ok) bildir(`${m} Kutuda ${adet} mesaj var.`);
      else bildir('', m);
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    }
  };

  const postalariOku = async () => {
    setOkuyor(true);
    bildir('Posta kutusu okunuyor…');
    try {
      const s = await epostaAPI.oku(40);
      const okunan = s.okunan ?? s.Okunan ?? 0;
      const eslesen = s.eslesen ?? s.Eslesen ?? 0;
      const h = s.hata ?? s.Hata;
      if (h) bildir('', h);
      else if (okunan === 0) bildir('Yeni posta yok.');
      else bildir(`${okunan} yeni posta okundu, ${eslesen} tanesi dosyayla eşleşti.`);
      await listeyiYukle();
      await hesabiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    } finally {
      setOkuyor(false);
    }
  };

  const onayla = async (y, aciklama, sonTarih, caseFileId) => {
    try {
      await epostaAPI.onayla({
        YazismaId: y.id ?? y.Id,
        CaseFileId: caseFileId || null,
        Aciklama: aciklama,
        SonTarih: sonTarih || null,
      });
      bildir('İş listesine eklendi.');
      setAcik(null);
      await listeyiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    }
  };

  const yoksay = async (y) => {
    try {
      await epostaAPI.yoksay(y.id ?? y.Id);
      await listeyiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    }
  };

  const topluYoksay = async () => {
    const idler = [...secililer];
    if (idler.length === 0) return;
    setTopluCalisiyor(true);
    try {
      const s = await epostaAPI.yoksayToplu(idler);
      bildir(`${s.adet ?? idler.length} posta yoksayıldı.`);
      await listeyiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    } finally {
      setTopluCalisiyor(false);
    }
  };

  const topluSil = async () => {
    const idler = [...secililer];
    if (idler.length === 0) return;
    if (!await onayla(
      `${idler.length} posta listeden kaldırılsın mı?\n\n`
      + 'Kayıt veritabanında saklanır, yalnızca listede görünmez.')) return;
    setTopluCalisiyor(true);
    try {
      const s = await epostaAPI.silToplu(idler);
      bildir(`${s.adet ?? idler.length} posta listeden kaldırıldı.`);
      await listeyiYukle();
    } catch (err) {
      bildir('', err.response?.data?.message || err.message);
    } finally {
      setTopluCalisiyor(false);
    }
  };

  return (
    <div className="gp">
      <header className="gp-basi">
        <div className="gp-basi-sol">
          <h1>Gelen Postalar</h1>
          <span className="gp-alt">
            Posta kutunuz arka planda kendiliğinden kontrol edilir ve gelen yazılar
            dosyalarınızla eşleştirilir. İş kaydı yalnızca siz onaylayınca oluşur.
          </span>
          <span className="gp-son-kontrol">
            <span className="gp-nabiz" aria-hidden="true" />
            Son kontrol: {gecenSure(sonKontrol)}
          </span>
        </div>
        <div className="gp-basi-sag">
          <button type="button" className="btn btn-secondary" onClick={() => setAyarAcik(!ayarAcik)}>
            {ayarAcik ? 'Ayarları Kapat' : 'Hesap Ayarları'}
          </button>
          <button type="button" className="btn btn-primary" onClick={postalariOku} disabled={okuyor || !hesap}>
            {okuyor ? 'Kontrol ediliyor…' : 'Şimdi Kontrol Et'}
          </button>
        </div>
      </header>

      <div className="gp-bildirim-alani" aria-live="polite">
        {mesaj && <p className="gp-bildirim">{mesaj}</p>}
        {hata && <p className="gp-bildirim gp-hata">{hata}</p>}
      </div>

      {ayarAcik && (
        <form className="gp-ayar" onSubmit={ayarKaydet}>
          <h2>E-posta Hesabı</h2>
          <p className="gp-ayar-not">
            Yalnızca <b>gelen kutusu</b> okunur; gönderilmiş ve diğer klasörlere
            dokunulmaz. Şifreniz sunucuda şifrelenerek saklanır ve hiçbir yerde
            görüntülenmez.
          </p>

          <div className="gp-ayar-satir">
            <label>
              E-posta adresi
              <input type="email" className="form-control" value={form.Eposta} required
                onChange={(e) => setForm({ ...form, Eposta: e.target.value })} />
            </label>
            <label>
              Kullanıcı adı
              <input type="text" className="form-control" value={form.KullaniciAdi} required
                onChange={(e) => setForm({ ...form, KullaniciAdi: e.target.value })} />
            </label>
          </div>

          <div className="gp-ayar-satir">
            <label>
              IMAP sunucusu
              <input type="text" className="form-control" value={form.ImapSunucu} required
                placeholder="imap.gmail.com"
                onChange={(e) => setForm({ ...form, ImapSunucu: e.target.value })} />
            </label>
            <label className="gp-dar">
              Port
              <input type="number" className="form-control" value={form.ImapPort}
                onChange={(e) => setForm({ ...form, ImapPort: parseInt(e.target.value, 10) || 993 })} />
            </label>
          </div>

          <div className="gp-ayar-satir">
            <label>
              Şifre <span className="gp-istege">(boş bırakırsanız mevcut şifre korunur)</span>
              <input type="password" className="form-control" value={form.Sifre} autoComplete="new-password"
                onChange={(e) => setForm({ ...form, Sifre: e.target.value })} />
            </label>
          </div>

          <div className="gp-ayar-onaylar">
            <label className="gp-onay">
              <input type="checkbox" checked={form.SslKullan}
                onChange={(e) => setForm({ ...form, SslKullan: e.target.checked })} />
              SSL kullan
            </label>
            <label className="gp-onay">
              <input type="checkbox" checked={form.Aktif}
                onChange={(e) => setForm({ ...form, Aktif: e.target.checked })} />
              Otomatik kontrol açık
            </label>
          </div>

          <div className="gp-ayar-dugmeler">
            <button type="button" className="btn btn-secondary" onClick={baglantiTest}>
              Bağlantıyı Test Et
            </button>
            <button type="submit" className="btn btn-primary">Kaydet</button>
          </div>

          {hesap?.sonHata && <p className="gp-bildirim gp-hata">Son hata: {hesap.sonHata}</p>}
        </form>
      )}

      <nav className="gp-sekmeler">
        {[
          ['bekliyor', 'Bekleyen'],
          ['onaylandi', 'İş listesine alınan'],
          ['yoksayildi', 'Yoksayılan'],
          ['hepsi', 'Hepsi'],
        ].map(([d, ad]) => {
          const s = sayim.find((x) => (x.durum ?? x.Durum) === d);
          const adet = s ? (s.adet ?? s.Adet) : null;
          return (
            <button key={d} type="button"
              className={'gp-sekme' + (durum === d ? ' aktif' : '')}
              onClick={() => durumSec(d)}>
              {ad}
              {adet > 0 && <span className="gp-sekme-adet">{adet}</span>}
            </button>
          );
        })}
      </nav>

      {/* Tarih süzgeci — seçim hatırlanır. */}
      <div className="gp-suzgec">
        <span className="gp-suzgec-etiket">Tarih</span>
        <div className="gp-suzgec-dugmeler">
          {GUN_SECENEKLERI.map((g) => (
            <button
              key={g.deger}
              type="button"
              className={'gp-suzgec-dugme' + (gun === g.deger ? ' aktif' : '')}
              onClick={() => gunSec(g.deger)}
            >
              {g.ad}
            </button>
          ))}
        </div>
        <span className="gp-suzgec-bilgi">
          {liste.length > 0 ? liste.length + ' posta' : ''}
        </span>
      </div>

      {/* Toplu işlem çubuğu — seçim yapılınca aşağıdan kayarak gelir. */}
      <div className={'gp-toplu' + (secililer.size > 0 ? ' acik' : '')}>
        <span className="gp-toplu-sayi">{secililer.size} posta seçildi</span>
        <div className="gp-toplu-dugmeler">
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => setSecililer(new Set())} disabled={topluCalisiyor}>
            Seçimi bırak
          </button>
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={topluYoksay} disabled={topluCalisiyor}>
            {topluCalisiyor ? 'İşleniyor…' : 'Yoksay'}
          </button>
          <button type="button" className="btn btn-danger btn-sm"
            onClick={topluSil} disabled={topluCalisiyor}>
            Listeden kaldır
          </button>
        </div>
      </div>

      {liste.length > 0 && (
        <label className="gp-hepsi">
          <input type="checkbox" checked={hepsiSecili} onChange={hepsiniSec} />
          Görünen {liste.length} postanın hepsini seç
        </label>
      )}

      {yukleniyor ? (
        <div className="gp-iskelet">
          {[0, 1, 2, 3].map((i) => <div key={i} className="gp-iskelet-satir" />)}
        </div>
      ) : liste.length === 0 ? (
        <p className="gp-bos">
          {gun > 0
            ? `Son ${gun === 1 ? '1 günde' : gun + ' günde'} bu sekmede posta yok. Tarih aralığını genişletebilirsiniz.`
            : durum === 'bekliyor' && bekleyen == null
              ? 'Bekleyen posta yok. Kutunuz arka planda kontrol ediliyor.'
              : 'Bu sekmede kayıt yok.'}
        </p>
      ) : (
        <ul className="gp-liste">
          {liste.map((y) => {
            const id = y.id ?? y.Id;
            const tur = y.eslesmeTuru ?? y.EslesmeTuru ?? 'yok';
            const foyNo = y.foyNo ?? y.FoyNo;
            const adaylar = y.adaylar ?? y.Adaylar ?? [];
            const acikMi = acik === id;
            const secili = secililer.has(id);

            return (
              <li key={id} className={'gp-satir' + (acikMi ? ' acik' : '') + (secili ? ' secili' : '')}>
                <div className="gp-satir-ust">
                  <label className="gp-sec" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={secili} onChange={() => secimDegistir(id)} />
                  </label>

                  <button type="button" className="gp-satir-bas" onClick={() => setAcik(acikMi ? null : id)}>
                    <span className="gp-konu">{y.konu ?? y.Konu ?? '(konusuz)'}</span>
                    <span className="gp-gonderen">
                      {y.gonderenAd ?? y.GonderenAd ?? y.gonderen ?? y.Gonderen ?? '-'}
                    </span>
                    <span className={'gp-eslesme '
                      + (foyNo ? 'var' : adaylar.length > 0 ? 'aday' : 'yok')}>
                      {foyNo
                        ? `Föy ${foyNo}`
                        : adaylar.length > 0
                          ? `${adaylar.length} aday dosya`
                          : ESLESME_ETIKET[tur] || 'Eşleşmedi'}
                    </span>
                    <span className="gp-tarih">{tarihYaz(y.gelisTarihi ?? y.GelisTarihi)}</span>
                    <span className="gp-ok" aria-hidden="true">›</span>
                  </button>
                </div>

                {acikMi && (
                  <PostaDetay yazisma={y} onOnayla={onayla} onYoksay={yoksay} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/** Tek bir postanın açılan kısmı: özet + aday dosyalar + iş kaydı. */
const PostaDetay = ({ yazisma, onOnayla, onYoksay }) => {
  const foyNo = yazisma.foyNo ?? yazisma.FoyNo;
  const muvekkil = yazisma.muvekkil ?? yazisma.Muvekkil;
  const durum = yazisma.durum ?? yazisma.Durum;
  const anahtar = yazisma.eslesmeAnahtari ?? yazisma.EslesmeAnahtari;
  const tur = yazisma.eslesmeTuru ?? yazisma.EslesmeTuru ?? 'yok';
  const adaylar = yazisma.adaylar ?? yazisma.Adaylar ?? [];

  const [aciklama, setAciklama] = useState('E-posta: ' + (yazisma.konu ?? yazisma.Konu ?? ''));
  const [sonTarih, setSonTarih] = useState('');
  const [dosyaId, setDosyaId] = useState(yazisma.caseFileId ?? yazisma.CaseFileId ?? '');

  const seciliAday = adaylar.find((a) => String(a.id ?? a.Id) === String(dosyaId));

  return (
    <div className="gp-detay">
      <div className="gp-detay-ust">
        <div className="gp-detay-bilgi">
          <span className="gp-etiket">Gönderen</span>
          <span>{yazisma.gonderen ?? yazisma.Gonderen ?? '-'}</span>
        </div>
        <div className="gp-detay-bilgi">
          <span className="gp-etiket">Eşleşme</span>
          <span>{ESLESME_ETIKET[tur] || 'Eşleşmedi'}</span>
        </div>
        {anahtar && (
          <div className="gp-detay-bilgi">
            <span className="gp-etiket">Bulunan</span>
            <span className="gp-anahtar">{anahtar}</span>
          </div>
        )}
        {(yazisma.ekSayisi ?? yazisma.EkSayisi) > 0 && (
          <div className="gp-detay-bilgi">
            <span className="gp-etiket">Ek</span>
            <span>{yazisma.ekSayisi ?? yazisma.EkSayisi} dosya</span>
          </div>
        )}
      </div>

      <p className="gp-ozet">{yazisma.ozet ?? yazisma.Ozet ?? '(içerik okunamadı)'}</p>

      {/* Aday dosyalar: posta müvekkil adıyla eşleştiğinde ya da birden
          fazla dosyaya işaret ettiğinde hepsi burada listelenir. */}
      {durum === 'bekliyor' && adaylar.length > 0 && (
        <div className="gp-adaylar">
          <span className="gp-adaylar-baslik">
            {foyNo ? 'Diğer olası dosyalar' : 'Hangi dosya olduğunu seçin'}
          </span>
          <div className="gp-aday-liste">
            {adaylar.map((a) => {
              const aid = a.id ?? a.Id;
              const sec = String(dosyaId) === String(aid);
              return (
                <button
                  key={aid}
                  type="button"
                  className={'gp-aday' + (sec ? ' aktif' : '')}
                  onClick={() => setDosyaId(sec ? '' : aid)}
                >
                  <span className="gp-aday-foy">Föy {a.foyNo ?? a.FoyNo}</span>
                  <span className="gp-aday-ad">{a.muvekkil ?? a.Muvekkil}</span>
                  <span className="gp-aday-tur">
                    {davaTuruEtiketi({ DavaTuru: a.davaTuru ?? a.DavaTuru })}
                    {(a.mahkemeEsas ?? a.MahkemeEsas) ? ' · ' + (a.mahkemeEsas ?? a.MahkemeEsas) : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {durum === 'bekliyor' ? (
        <div className="gp-islem">
          <label>
            İş açıklaması
            <input type="text" className="form-control" value={aciklama}
              onChange={(e) => setAciklama(e.target.value)} />
          </label>
          <label className="gp-dar">
            Son tarih <span className="gp-istege">(isteğe bağlı)</span>
            <input type="date" className="form-control" value={sonTarih}
              onChange={(e) => setSonTarih(e.target.value)} />
          </label>

          <div className="gp-islem-dugmeler">
            <span className="gp-hedef">
              {seciliAday
                ? `Föy ${seciliAday.foyNo ?? seciliAday.FoyNo} — ${seciliAday.muvekkil ?? seciliAday.Muvekkil}`
                : foyNo
                  ? `Föy ${foyNo} — ${muvekkil || ''}`
                  : 'Dosya seçilmedi'}
            </span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onYoksay(yazisma)}>
              Yoksay
            </button>
            <button type="button" className="btn btn-primary btn-sm"
              onClick={() => onOnayla(yazisma, aciklama, sonTarih, dosyaId ? parseInt(dosyaId, 10) : null)}>
              İş Listesine Ekle
            </button>
          </div>
        </div>
      ) : (
        <p className="gp-islem-bilgi">
          {durum === 'onaylandi'
            ? `İş listesine aktarıldı (iş no ${yazisma.olusanJobId ?? yazisma.OlusanJobId}).`
            : 'Yoksayıldı.'}
        </p>
      )}
    </div>
  );
};

export default GelenPostalar;
